import { supabase } from '../../config/supabase';

/**
 * Create a new job
 * @param {Object} jobData - Job data
 * @param {string} companyId - Company ID
 * @param {string} createdByUserId - User ID who created the job
 * @param {string} createdByName - Name of user who created the job
 * @returns {Promise<Object>} Created job
 */
export const createJob = async (jobData, companyId, createdByUserId, createdByName, companySlug = null) => {
    try {

        // 1. Get current job count for the company
        // const { count, error: countError } = await supabase
        //     .from('jobs')
        //     .select('*', { count: 'exact', head: true })
        //     .eq('company_id', companyId)
        //     .eq('deleted', false)
        //     .neq('job_type', 'Timber');

        // if (countError) throw countError;

        // let editedUniqueId = jobData?.uniqueId || ""

        // If useCustomId is true, use the provided uniqueId as-is without auto-generation
        // if (jobData?.useCustomId && jobData?.uniqueId?.trim()) {
        //     editedUniqueId = jobData.uniqueId.trim();
        // } else if (jobData?.jobType !== "Timber") {
        //     // 2. Generate the prefixed unique_id
        //     if (companySlug && companySlug == "melbourne-truss") {
        //         const jobNumber = (count || 0) + 1 + 231;
        //         editedUniqueId = `N${jobNumber}`
        //     } else {
        //         const jobNumber = (count || 0) + 1;
        //         editedUniqueId = `N${jobNumber}`
        //     }

        //     if (jobData?.uniqueId?.trim()) editedUniqueId += `-${jobData?.uniqueId}`
        // }

        const selectedDepartments = Object.entries(jobData.quotes)
            .filter(([, q]) => q.isRequired)
            .map(([type]) => type);  // e.g. ['roof', 'wall', 'floor']

        const storey = jobData.jobInformation?.isSingleStorey === false
            ? (jobData.jobInformation?.isTripleStorey ? 'triple' : 'double')
            : 'single';

        // 3. Create the main job
        const { data: job, error: jobError } = await supabase
            .from('jobs')
            .insert({
                company_id: companyId,
                unique_id: jobData.uniqueId.trim(),
                customer_name: jobData.customerName,
                customer_number: jobData.customerNumber ? `+61${jobData.customerNumber}` : null,
                customer_mail: jobData.customerMail || null,
                carpenter_supervisor_name: jobData.carpenterSupervisorName || null,
                carpenter_supervisor_number: jobData.carpenterSupervisorNumber ? `+61${jobData.carpenterSupervisorNumber}` : null,
                customer_address: jobData.customerAddress || null,
                job_type: jobData.jobType,
                main_status: jobData.jobType === 'Timber' ? 'ready_for_delivery' :
                    jobData.jobType === 'Estimation' ? 'pending_estimation' : 'pending_design',
                booking_delivery_date: jobData.bookingDeliveryDate || null,
                expected_estimation_finish_date: jobData.estimationFinishDate || null,
                roof_type: jobData.jobInformation?.roofType || null,
                roof_truss_centres: jobData.jobInformation?.roofTrussCentres || null,
                is_single_storey: jobData.jobInformation?.isSingleStorey ?? true,
                is_unit: jobData.jobInformation?.isUnit || false,
                unit_count: jobData.jobInformation?.unitCount || null,
                urgent_quote: jobData.jobInformation?.urgentQuote || false,
                urgent_design: jobData.jobInformation?.urgentDesign || false,
                site_measure_required: jobData.jobInformation?.siteMeasure?.required || false,
                site_measure_wall: jobData.jobInformation?.siteMeasure?.wall || false,
                site_measure_floor: jobData.jobInformation?.siteMeasure?.floor || false,
                site_measure_truss: jobData.jobInformation?.siteMeasure?.truss || false,
                created_by_user_id: createdByUserId,
                created_by_name: createdByName,
                selected_departments: selectedDepartments,
                storey,
                is_parent: true
            })
            .select()
            .single();

        if (jobError) throw jobError;

        // NOTE: job_quotes are created automatically by the DB trigger on jobs insert.
        if (jobData.jobType === 'Timber') {
            const { error: timberQuoteError } = await supabase
                .from('job_quotes')
                .insert({
                    job_id: job.id,
                    quote_type: 'timber',
                    is_required: true,
                    design_status: 'not_required',
                    estimation_status: 'not_required',
                    production_status: 'not_required',
                    delivery_status: 'not_scheduled',
                    invoiced: jobData.quotes?.timber?.invoiced || false,
                    expected_delivery_date: jobData.quotes?.timber?.expectedDeliveryDate || null,
                    expected_finish_date: jobData.quotes?.timber?.expectedDeliveryDate || null,
                });
            if (timberQuoteError) throw timberQuoteError;
        }

        const fileOnlyQuoteTypes = ["beams", "flooring_sheets"];

        const fileOnlyQuotesToInsert = fileOnlyQuoteTypes
            .filter(type => jobData.quotes?.[type]?.isRequired)
            .map(type => ({
                job_id: job.id,
                quote_type: type,
                is_required: true,
                design_status: "not_required",
                estimation_status: "not_required",
                production_status: "not_started",
                delivery_status: "not_scheduled",
                invoiced: false,
                child_unique_id: type === "beams"
                    ? `${job.unique_id}B`
                    : `${job.unique_id}FS`,
                department_file_status: "waiting_files",
            }));

        if (fileOnlyQuotesToInsert.length > 0) {
            const { error: fileOnlyQuotesError } = await supabase
                .from("job_quotes")
                .insert(fileOnlyQuotesToInsert);

            if (fileOnlyQuotesError) throw fileOnlyQuotesError;
        }


        for (const type of fileOnlyQuoteTypes) {
            const files = jobData.quotes?.[type]?.files || [];

            if (!jobData.quotes?.[type]?.isRequired || files.length === 0) continue;

            await uploadJobFiles(
                job.id,
                files,
                createdByUserId,
                createdByName,
                companySlug,
                "initial",
                type
            );

            await supabase
                .from("job_quotes")
                .update({
                    department_file_status: "completed",
                    department_files_completed_at: new Date().toISOString(),
                    department_files_completed_by: createdByUserId,
                    department_files_completed_by_name: createdByName,
                })
                .eq("job_id", job.id)
                .eq("quote_type", type);
        }

        // 3. Create floor specifications if wall is required
        if (jobData.quotes.wall?.isRequired) {
            const floorSpecsToInsert = [];

            if (jobData.jobInformation.isSingleStorey) {
                floorSpecsToInsert.push({
                    job_id: job.id,
                    floor_level: 'single',
                    load_bearing_wall_stud_sizes: jobData.jobInformation.singleStorey?.loadBearingWallStudSizes || '90x45',
                    non_load_bearing_wall_stud_sizes: jobData.jobInformation.singleStorey?.nonLoadBearingWallStudSizes || null,
                    non_load_bearing_wall_stud_centres: jobData.jobInformation.singleStorey?.nonLoadBearingWallStudCentres || null,
                    wall_height: jobData.jobInformation.singleStorey?.wallHeight || null
                });
            } else {
                // Ground floor
                floorSpecsToInsert.push({
                    job_id: job.id,
                    floor_level: 'ground',
                    load_bearing_wall_stud_sizes: jobData.jobInformation.doubleStorey?.groundFloor?.loadBearingWallStudSizes || '90x45',
                    non_load_bearing_wall_stud_sizes: jobData.jobInformation.doubleStorey?.groundFloor?.nonLoadBearingWallStudSizes || null,
                    non_load_bearing_wall_stud_centres: jobData.jobInformation.doubleStorey?.groundFloor?.nonLoadBearingWallStudCentres || null,
                    wall_height: jobData.jobInformation.doubleStorey?.groundFloor?.wallHeight || null
                });

                // First floor
                floorSpecsToInsert.push({
                    job_id: job.id,
                    floor_level: 'first',
                    load_bearing_wall_stud_sizes: jobData.jobInformation.doubleStorey?.firstFloor?.loadBearingWallStudSizes || '90x45',
                    non_load_bearing_wall_stud_sizes: jobData.jobInformation.doubleStorey?.firstFloor?.nonLoadBearingWallStudSizes || null,
                    non_load_bearing_wall_stud_centres: jobData.jobInformation.doubleStorey?.firstFloor?.nonLoadBearingWallStudCentres || null,
                    wall_height: jobData.jobInformation.doubleStorey?.firstFloor?.wallHeight || null
                });
            }

            const { error: specsError } = await supabase
                .from('job_floor_specs')
                .insert(floorSpecsToInsert);

            if (specsError) throw specsError;
        }

        // 4. Create floors if floor quote is required
        if (jobData.quotes.floor?.isRequired && jobData.jobInformation.floors?.length > 0) {
            const floorsToInsert = jobData.jobInformation.floors.map(floor => ({
                job_id: job.id,
                floor_number: floor.floorNumber,
                floor_truss_size: floor.floorTrussSize || null,
                flooring: floor.flooring || null,
                balcony_included: floor.balconyIncluded || false
            }));

            const { error: floorsError } = await supabase
                .from('job_floors')
                .insert(floorsToInsert);

            if (floorsError) throw floorsError;
        }

        // 5. Create notes
        if (jobData.jobInformation.notes?.length > 0) {
            const notesToInsert = jobData.jobInformation.notes.map(note => ({
                job_id: job.id,
                content: note.content,
                added_by_user_id: createdByUserId,
                added_by_name: createdByName,
                added_by_role: 'admin', // or get from user profile
                added_at: note.addedAt || new Date().toISOString()
            }));

            const { error: notesError } = await supabase
                .from('job_notes')
                .insert(notesToInsert);

            if (notesError) throw notesError;
        }

        // Create custom folders
        const folders = [
            {
                job_id: job.id,
                filename: '__folder_placeholder__',
                original_name: "Quotes & Invoices",
                url: '__folder_placeholder__',
                uploaded_by_user_id: createdByUserId,
                uploaded_by_name: createdByName,
                uploaded_by_role: "admin",
                file_type: 'custom',
                description: "Quotes & Invoices",
                placeholder_by_default: true
            },
            {
                job_id: job.id,
                filename: '__folder_placeholder__',
                original_name: "Layouts & Comps",
                url: '__folder_placeholder__',
                uploaded_by_user_id: createdByUserId,
                uploaded_by_name: createdByName,
                uploaded_by_role: "admin",
                file_type: 'custom',
                description: "Layouts & Comps",
                placeholder_by_default: true
            }
        ];

        const { error: folderError } = await supabase
            .from('job_files')
            .insert(folders);

        if (folderError) throw folderError;

        // 6. Create action log — captures everything entered on the New Job form
        const formatWallSpec = (spec) => spec
            ? `studs ${spec.loadBearingWallStudSizes || '-'}/${spec.nonLoadBearingWallStudSizes || '-'}, centres ${spec.nonLoadBearingWallStudCentres || '-'}, height ${spec.wallHeight || '-'}`
            : null;

        let wallSpecsSummary = null;
        if (jobData.quotes.wall?.isRequired) {
            if (storey === 'single') {
                wallSpecsSummary = formatWallSpec(jobData.jobInformation?.singleStorey);
            } else if (storey === 'double') {
                wallSpecsSummary = `Ground: ${formatWallSpec(jobData.jobInformation?.doubleStorey?.groundFloor) || '-'} | First: ${formatWallSpec(jobData.jobInformation?.doubleStorey?.firstFloor) || '-'}`;
            } else {
                wallSpecsSummary = `Ground: ${formatWallSpec(jobData.jobInformation?.tripleStorey?.groundFloor) || '-'} | First: ${formatWallSpec(jobData.jobInformation?.tripleStorey?.firstFloor) || '-'} | Second: ${formatWallSpec(jobData.jobInformation?.tripleStorey?.secondFloor) || '-'}`;
            }
        }

        const floorsSummary = jobData.quotes.floor?.isRequired && jobData.jobInformation?.floors?.length
            ? jobData.jobInformation.floors
                .map(f => `#${f.floorNumber} ${f.floorTrussSize || '-'}/${f.flooring || '-'}${f.balconyIncluded ? ' +balcony' : ''}`)
                .join('; ')
            : null;

        const departmentDetails = selectedDepartments
            .map(type => {
                const q = jobData.quotes?.[type];
                const date = q?.expectedDeliveryDate || q?.expectedFinishDate || null;
                return date ? `${type} (${date})` : type;
            })
            .join(', ') || null;

        const notesSummary = jobData.jobInformation?.notes?.length
            ? jobData.jobInformation.notes.map(n => n.content).join(' | ')
            : null;

        const newValues = {
            unique_id: job.unique_id,
            customer_name: jobData.customerName || null,
            customer_number: jobData.customerNumber ? `+61${jobData.customerNumber}` : null,
            customer_mail: jobData.customerMail || null,
            contact_name: jobData.contactName || null,
            customer_address: jobData.customerAddress || null,
            carpenter_supervisor_name: jobData.carpenterSupervisorName || null,
            carpenter_supervisor_number: jobData.carpenterSupervisorNumber ? `+61${jobData.carpenterSupervisorNumber}` : null,
            job_type: jobData.jobType,
            selected_departments: selectedDepartments,
            department_details: departmentDetails,
            roof_type: jobData.jobInformation?.roofType || null,
            roof_truss_centres: jobData.jobInformation?.roofTrussCentres || null,
            storey,
            wall_specs: wallSpecsSummary,
            floors: floorsSummary,
            is_unit: jobData.jobInformation?.isUnit || false,
            unit_count: jobData.jobInformation?.unitCount || null,
            urgent_quote: jobData.jobInformation?.urgentQuote || false,
            urgent_design: jobData.jobInformation?.urgentDesign || false,
            site_measure_required: jobData.jobInformation?.siteMeasure?.required || false,
            site_measure_wall: jobData.jobInformation?.siteMeasure?.wall || false,
            site_measure_floor: jobData.jobInformation?.siteMeasure?.floor || false,
            site_measure_truss: jobData.jobInformation?.siteMeasure?.truss || false,
            expected_estimation_finish_date: jobData.estimationFinishDate || null,
            notes: notesSummary,
            files_count: jobData.pdfs?.length || 0,
        };

        const changedFields = Object.keys(newValues);

        const { error: logError } = await supabase
            .from('job_action_logs')
            .insert({
                job_id: job.id,
                company_id: companyId,
                user_id: createdByUserId,
                user_name: createdByName,
                user_role: 'admin',
                action: 'job_created',
                details: `Job ${job.unique_id} created for ${jobData.customerName || 'unknown customer'} (${jobData.jobType}) — departments: ${selectedDepartments.join(', ') || 'none'}`,
                action_origin: 'new-assignment',
                changed_fields: changedFields,
                old_values: {},
                new_values: newValues,
            });

        if (logError) throw logError;


        // Update department dates
        const quoteDateUpdates = [];

        Object.entries(jobData.quotes).forEach(([type, dep]) => {
            if (!dep.isRequired) return;

            const expectedFinish = dep.expectedDeliveryDate || dep.expectedFinishDate || null;
            const expectedDelivery = dep.expectedDeliveryDate || dep.expectedFinishDate || null;

            quoteDateUpdates.push({
                quote_type: type,
                expected_finish_date: expectedDelivery || null,
                expected_delivery_date: expectedDelivery || null
            });
        })

        for (const update of quoteDateUpdates) {
            const { error } = await supabase
                .from("job_quotes")
                .update({
                    expected_finish_date: update?.expected_delivery_date,
                    expected_delivery_date: update?.expected_delivery_date
                })
                .eq("job_id", job.id)
                .eq("quote_type", update.quote_type);

            if (error) throw error;
        }

        return { job, message: 'Job created successfully' };
    } catch (error) {
        console.error('Error creating job:', error);
        throw error;
    }
};

/**
 * Get customers for a company
 * @param {string} companyId - Company ID
 * @returns {Promise<Array>} List of customers
 */
export const getCustomers = async (companyId) => {
    try {
        const { data, error } = await supabase
            .from('customers')
            .select('id, name, phone, email, contact_name')
            .eq('company_id', companyId)
            .order('name', { ascending: true });
        if (error) throw error;
        return data || [];
    } catch (error) {
        console.error('Error fetching customers:', error);
        throw error;
    }
};

/**
 * Add a new customer
 * @param {Object} customerData - Customer data
 * @param {string} companyId - Company ID
 * @returns {Promise<Object>} Created customer
 */
export const addCustomer = async (customerData, companyId) => {
    try {
        // Implement customer creation if you have a customers table
        // For now, returning success message
        return { message: 'Customer added successfully', customer: customerData };
    } catch (error) {
        console.error('Error adding customer:', error);
        throw error;
    }
};

/**
 * Upload files for a job
 * @param {string} jobId - Job ID
 * @param {Array} files - Array of files to upload
 * @param {string} uploadedByUserId - User ID who uploaded
 * @param {string} uploadedByName - Name of uploader
 * @returns {Promise<Array>} Uploaded file records
 */
export const uploadJobFiles = async (jobId, files, uploadedByUserId, uploadedByName, companySlug, category = null, quoteType = null) => {
    try {
        const uploadedFiles = [];

        for (const file of files) {
            // 1. Upload file to Supabase Storage
            const fileName = `${Date.now()}-${file.name}`;
            const { data: uploadData, error: uploadError } = await supabase.storage
                .from('job-files') // You need to create this bucket in Supabase
                .upload(`${companySlug}/${jobId}/${fileName}`, file);

            if (uploadError) throw uploadError;

            // 2. Get public URL
            const { data: { publicUrl } } = supabase.storage
                .from('job-files')
                .getPublicUrl(`${companySlug}/${jobId}/${fileName}`);

            // 3. Create file record in database
            const { data: fileRecord, error: recordError } = await supabase
                .from('job_files')
                .insert({
                    job_id: jobId,
                    filename: fileName,
                    original_name: file.name,
                    url: publicUrl,
                    uploaded_by_user_id: uploadedByUserId,
                    uploaded_by_name: uploadedByName,
                    uploaded_by_role: 'admin',
                    file_type: category || 'initial',
                    quote_type: quoteType || null
                })
                .select()
                .single();

            if (recordError) throw recordError;

            uploadedFiles.push(fileRecord);
        }

        // 4. Create action log for file uploads
        if (uploadedFiles.length > 0) {
            const { error: logError } = await supabase
                .from('job_action_logs')
                .insert({
                    job_id: jobId,
                    user_id: uploadedByUserId,
                    user_name: uploadedByName,
                    user_role: 'admin',
                    action: 'job_files_uploaded',
                    details: `${uploadedFiles.length} file(s) uploaded: ${uploadedFiles.map(f => f.original_name).join(', ')}`,
                    changed_fields: ['files'],
                });

            if (logError) throw logError;
        }


        return uploadedFiles;
    } catch (error) {
        console.error('Error uploading files:', error);
        throw error;
    }
};


/**
 * Fetch assignments with server-side filters, sorting and pagination
 * Mirrors the pattern used by getEditorTableData in schedule.js
 */
// Read all matching rows, including when the API caps a response below our
// requested range. Never silently apply a partial completion/invoice filter.
const readAssignmentFilterRows = async (makeQuery) => {
    const rows = [];
    for (;;) {
        const { data, error } = await makeQuery().order('id').range(rows.length, rows.length + 499);
        if (error) throw error;
        if (!data?.length) return rows;
        rows.push(...data);
    }
};

export const getAssignmentsData = async (companyId, page = 1, limit = 50, filters = {}) => {
    try {
        const offset = (page - 1) * limit;

        let query = supabase
            .from('jobs')
            .select(`
                *,
                job_quotes(*),
                job_designers(*),
                job_estimators(*, estimator_user:users!job_estimators_user_id_fkey(username, full_name)),
                job_production(*),
                job_deliveries(*)
            `, { count: 'exact' })
            .eq('company_id', companyId)
            .eq('deleted', false);

        if (!filters.showCancelled && filters.statusFilter !== 'cancelled') {
            query = query.or('is_cancelled.is.null,is_cancelled.eq.false');
        }

        // ── status filter ──────────────────────────────────────────────────
        if (filters.statusFilter === 'cancelled') {
            query = query.eq('is_cancelled', true);
        }
        else if (filters.statusFilter && filters.statusFilter !== 'all') {
            query = query
                .or('is_cancelled.is.null,is_cancelled.eq.false')
                .eq('main_status', filters.statusFilter);
        }

        const jobIdsToExclude = new Set();
        if (filters.hideEstCompleted) {
            const rows = await readAssignmentFilterRows(() => supabase
                .from('job_estimators')
                .select('id, job_id, jobs!inner(company_id)')
                .eq('jobs.company_id', companyId)
                .eq('is_completed', true));
            rows.forEach(row => jobIdsToExclude.add(row.job_id));
        }
        if (filters.hideDesignCompleted) {
            const rows = await readAssignmentFilterRows(() => supabase
                .from('job_quotes')
                .select('id, job_id, design_status, jobs!inner(company_id)')
                .eq('jobs.company_id', companyId)
                .eq('is_required', true));
            const byJob = new Map();
            rows.filter(row => row.design_status !== 'not_required').forEach(row => {
                if (!byJob.has(row.job_id)) byJob.set(row.job_id, []);
                byJob.get(row.job_id).push(row.design_status);
            });
            byJob.forEach((statuses, id) => {
                if (statuses.length && statuses.every(status => status === 'completed')) jobIdsToExclude.add(id);
            });
        }
        if (jobIdsToExclude.size) query = query.not('id', 'in', `(${[...jobIdsToExclude].join(',')})`);

        // ── job type filter ────────────────────────────────────────────────
        if (filters.jobTypeFilter && filters.jobTypeFilter !== 'all') {
            if (filters.jobTypeFilter === 'design_only') query = query.eq('job_type', 'Design');
            else if (filters.jobTypeFilter === 'estimation_only') query = query.eq('job_type', 'Estimation');
            else if (filters.jobTypeFilter === 'design_estimation_only') query = query.eq('job_type', 'Design & Estimation');
            else if (filters.jobTypeFilter === 'timber_only') query = query.eq('job_type', 'Timber');
        }

        // ── urgent filter ──────────────────────────────────────────────────
        if (filters.urgentFilter && filters.urgentFilter !== 'all') {
            if (filters.urgentFilter === 'urgent_quote') query = query.eq('urgent_quote', true);
            else if (filters.urgentFilter === 'urgent_design') query = query.eq('urgent_design', true);
            else if (filters.urgentFilter === 'urgent_any') query = query.or('urgent_quote.eq.true,urgent_design.eq.true');
        }

        // ── quote sent filter ──────────────────────────────────────────────
        if (filters.quoteSentFilter && filters.quoteSentFilter !== 'all') {
            if (filters.quoteSentFilter === 'sent') query = query.eq('quote_sent', true);
            else if (filters.quoteSentFilter === 'not_sent') query = query.eq('quote_sent', false);
        }

        // ── on hold filter ─────────────────────────────────────────────────
        if (filters.onHoldFilter && filters.onHoldFilter !== 'all') {
            if (filters.onHoldFilter === 'on_hold') query = query.eq('on_hold', true);
            else if (filters.onHoldFilter === 'active') query = query.eq('on_hold', false);
        }

        // ── invoice filter (two-step: query job_quotes) ────────────────────
        if (filters.invoiceFilter && filters.invoiceFilter !== 'all') {
            const quoteRows = await readAssignmentFilterRows(() => supabase
                .from('job_quotes')
                .select('id, job_id, invoiced, jobs!inner(company_id)')
                .eq('jobs.company_id', companyId)
                .eq('is_required', true));
            if (!quoteRows.length) return { data: [], pagination: { page, limit, total: 0, hasMore: false } };

            if (quoteRows?.length) {
                const byJob = quoteRows.reduce((acc, q) => {
                    if (!acc[q.job_id]) acc[q.job_id] = [];
                    acc[q.job_id].push(q.invoiced);
                    return acc;
                }, {});

                let matchingIds = [];
                if (filters.invoiceFilter === 'all_invoiced') {
                    matchingIds = Object.entries(byJob)
                        .filter(([, statuses]) => statuses.length > 0 && statuses.every(s => s === true))
                        .map(([id]) => id);
                } else if (filters.invoiceFilter === 'partial') {
                    matchingIds = Object.entries(byJob)
                        .filter(([, statuses]) => statuses.some(s => s === true) && !statuses.every(s => s === true))
                        .map(([id]) => id);
                } else if (filters.invoiceFilter === 'not_invoiced') {
                    matchingIds = Object.entries(byJob)
                        .filter(([, statuses]) => statuses.every(s => s === false))
                        .map(([id]) => id);
                }

                if (matchingIds.length > 0) {
                    query = query.in('id', matchingIds);
                } else {
                    // Nothing matches — return empty
                    return { data: [], pagination: { page, limit, total: 0, hasMore: false } };
                }
            }
        }
        // ── extra department filters, AND mode ─────────────────────────────────────
        const extraDepartmentFilters = [];

        if (filters.beamsFilter) {
            extraDepartmentFilters.push("beams");
        }

        if (filters.flooringSheetsFilter) {
            extraDepartmentFilters.push("flooring_sheets");
        }

        if (extraDepartmentFilters.length > 0) {
            const quoteRows = await readAssignmentFilterRows(() => supabase
                .from("job_quotes")
                .select("id, job_id, quote_type, jobs!inner(company_id)")
                .eq("jobs.company_id", companyId)
                .eq("is_required", true)
                .in("quote_type", extraDepartmentFilters));

            const byJob = (quoteRows || []).reduce((acc, row) => {
                if (!acc[row.job_id]) acc[row.job_id] = new Set();
                acc[row.job_id].add(row.quote_type);
                return acc;
            }, {});

            const matchingIds = Object.entries(byJob)
                .filter(([, types]) => extraDepartmentFilters.every(type => types.has(type)))
                .map(([jobId]) => jobId);

            if (matchingIds.length > 0) {
                query = query.in("id", matchingIds);
            } else {
                return {
                    data: [],
                    pagination: {
                        page,
                        limit,
                        total: 0,
                        hasMore: false,
                    },
                };
            }
        }
        // ── search (server-side text match) ───────────────────────────────
        if (filters.searchQuery && filters.searchQuery.trim()) {
            const q = JSON.stringify(`%${filters.searchQuery.trim()}%`);
            query = query.or(
                `unique_id.ilike.${q},customer_name.ilike.${q},carpenter_supervisor_name.ilike.${q},customer_address.ilike.${q}`
            );
        }

        // ── sorting ────────────────────────────────────────────────────────
        const sortBy = filters.sortBy || 'newest';
        if (sortBy === 'oldest') {
            query = query.order('created_at', { ascending: true });
        } else if (sortBy === 'priority') {
            query = query
                .order('urgent_quote', { ascending: false, nullsFirst: false })
                .order('urgent_design', { ascending: false, nullsFirst: false })
                .order('created_at', { ascending: false });
        } else if (sortBy === 'alphabetical') {
            query = query
                .order('uid_prefix', { ascending: true })
                .order('uid_number', { ascending: true, nullsFirst: false });
        } else if (sortBy === 'reverse_alphabetical') {
            query = query
                .order('uid_prefix', { ascending: false })
                .order('uid_number', { ascending: false, nullsFirst: true });
        } else {
            query = query.order('created_at', { ascending: false });
        }

        // ── pagination ─────────────────────────────────────────────────────
        query = query.order('id', { ascending: true }).range(offset, offset + limit - 1);

        const { data, error, count } = await query;
        if (error) throw error;

        return {
            data: data || [],
            pagination: {
                page,
                limit,
                total: count || 0,
                hasMore: count ? (page * limit) < count : false,
            },
        };
    } catch (error) {
        console.error('Error fetching assignments data:', error);
        throw error;
    }
};

/**
 * Fetch all jobs/assignments for a company
 * @param {string} companyId - The company ID
 * @returns {Promise<Array>} Array of job assignments
 */
export const getAssignments = async (companyId) => {
    try {
        const { data, error } = await supabase
            .from('jobs')
            .select(`
                *,
                job_quotes(*),
                job_designers(*),
                job_estimators(*, estimator_user:users!job_estimators_user_id_fkey(username, full_name)),
                job_production(*),
                job_deliveries(*)
            `)
            .eq('company_id', companyId)
            .eq('deleted', false)
            .order('created_at', { ascending: false });

        if (error) throw error;
        return data || [];
    } catch (error) {
        console.error('Error fetching assignments:', error);
        throw error;
    }
};

/**
 * Subscribe to real-time job updates
 * @param {string} companyId - The company ID
 * @param {Function} callback - Callback function for updates
 * @returns {Object} Supabase channel subscription
 */
// Dedicated to this list: other callers keep their existing subscription contract.
export const subscribeToAssignmentListUpdates = (companyId, callback) => {
    const channel = supabase.channel(`assignment-list-${companyId}`)
        .on('postgres_changes', { event: '*', schema: 'public', table: 'jobs', filter: `company_id=eq.${companyId}` }, callback);
    // These child tables have job_id rather than company_id. RLS limits event
    // visibility; an unrelated visible event only triggers a company-scoped read.
    ['job_quotes', 'job_estimators'].forEach(table => {
        channel.on('postgres_changes', { event: '*', schema: 'public', table }, callback);
    });
    return channel.subscribe(status => {
        if (status === 'SUBSCRIBED') callback();
    });
};

export const subscribeToJobUpdates = (companyId, callback) => {
    const channel = supabase
        .channel(`jobs-changes-${companyId}`)
        .on(
            'postgres_changes',
            {
                event: '*',
                schema: 'public',
                table: 'jobs',
                filter: `company_id=eq.${companyId}`
            },
            callback
        )
        .subscribe();

    return channel;
};

/**
 * Unsubscribe from job updates
 */
export const unsubscribeFromJobUpdates = async (channel) => {
    if (channel) {
        await supabase.removeChannel(channel);
    }
};

/**
 * Get required quote types for a job
 * @param {Object} job - Job object with job_quotes
 * @returns {Array<string>} Array of required quote types (unique)
 */
export const getRequiredQuotes = (job) => {
    const quotes = [];
    const seenTypes = new Set();

    job.job_quotes?.forEach(quote => {
        if (quote.is_required && !seenTypes.has(quote.quote_type)) {
            quotes.push(quote.quote_type);
            seenTypes.add(quote.quote_type);
        }
    });
    return quotes;
};

/**
 * Transform job data from snake_case to camelCase
 */
const transformJobData = (job) => {
    // ── Status priority for aggregation (lower index = shown first) ───────────
    const STATUS_PRIORITY = [
        'in_progress', 'waiting_assignment', 'waiting_design',
        'not_started', 'in_cutting', 'cutting_ended', 'in_production',
        'scheduled', 'in_transit', 'completed', 'not_required',
    ];
    const aggStatus = (statuses) => {
        const valid = statuses.filter(Boolean);
        if (!valid.length) return null;
        return valid.reduce((best, cur) => {
            const bi = STATUS_PRIORITY.indexOf(best);
            const ci = STATUS_PRIORITY.indexOf(cur);
            // prefer the one with lower index; unknown statuses (-1) go last
            if (ci === -1) return best;
            if (bi === -1) return cur;
            return ci < bi ? cur : best;
        });
    };

    // Transform quotes
    const quotes = {};
    if (job.job_quotes) {
        // ── Step 1: only process primary/main quotes (not delivery sub-quotes) ─
        // delivery_count === null  → main quote (old + new jobs)
        // delivery_count === 0    → also treated as main quote
        // delivery_count >=1      → re-delivery sub-quote → skip
        const mainQuotes = job.job_quotes.filter(
            q => q.delivery_count === null || q.delivery_count === 0
        );

        // ── Step 2: group by quote_type ────────────────────────────────────────
        const byType = {};
        mainQuotes.forEach(q => {
            if (!byType[q.quote_type]) byType[q.quote_type] = [];
            byType[q.quote_type].push(q);
        });

        // ── Step 3: build one aggregate entry per type ─────────────────────────
        Object.entries(byType).forEach(([quoteType, quoteList]) => {
            // Only is_required=true rows drive status, designers, and isRequired flag.
            // Disabled child quotes (is_required=false) are excluded so they don't
            // pollute aggregate statuses or inflate assignee counts.
            // Old jobs: a single row with is_required=false means "unchecked" → correct.
            const requiredList = quoteList.filter(q => q.is_required);
            const first = requiredList[0] || quoteList[0]; // prefer first required row

            // Designers — only from required child quotes
            //   New jobs  (child_unique_id set): match by child_job_id === quote.id
            //   Old jobs  (no child_unique_id) : match by quote_type with no child_job_id
            const designers = [];
            const seenKey = new Set();
            requiredList.forEach(q => {
                const isNewQuote = !!q.child_unique_id;
                const qDesigners = job.job_designers
                    ?.filter(d => isNewQuote
                        ? d.child_job_id === q.id
                        : d.quote_type === quoteType && !d.child_job_id
                    )
                    ?.map(d => ({
                        userId: d.user_id,
                        name: d.name,
                        isStarted: d.is_started,
                        isCompleted: d.is_completed,
                        childJobId: d.child_job_id,
                        childName: d.child_unique_id,
                    })) || [];

                qDesigners.forEach(d => {
                    // deduplicate: new jobs by userId+childJobId, old by userId
                    const dedupeKey = d.childJobId
                        ? `${d.userId}_${d.childJobId}`
                        : String(d.userId);
                    if (!seenKey.has(dedupeKey)) {
                        seenKey.add(dedupeKey);
                        designers.push(d);
                    }
                });
            });

            // Production — keyed by quoteType (unchanged)
            const prodRecord = job.job_production?.find(p => p.quote_type === quoteType);
            const production = prodRecord ? {
                phase: prodRecord.phase,
                userId: prodRecord.user_id,
                name: prodRecord.name,
                isStarted: prodRecord.is_started_production,
                isCompleted: prodRecord.is_completed,
                startCuttingAt: prodRecord.start_cutting_at,
                endCuttingAt: prodRecord.end_cutting_at,
                startProductionAt: prodRecord.start_production_at,
                endProductionAt: prodRecord.end_production_at,
            } : null;

            // Delivery — first primary delivery for this type
            const delRecord = job.job_deliveries?.find(
                d => d.quote_type === quoteType && (!d.child_unique_id || d.child_unique_id === first.id)
            );
            const delivery = delRecord ? {
                isRequired: delRecord.is_required,
                deliveryStatus: delRecord.delivery_status,
                deliveryDate: delRecord.delivery_date,
                assignedDriver: delRecord.assigned_driver_id ? {
                    driverId: delRecord.assigned_driver_id,
                    driverName: delRecord.assigned_driver_name,
                    assignedAt: delRecord.assigned_at,
                    assignedBy: delRecord.assigned_by_name ? { name: delRecord.assigned_by_name } : null,
                } : null,
                deliveryNotes: delRecord.delivery_notes,
            } : null;

            const aggregatedEntry = {
                // isRequired only true when at least one child quote is required
                isRequired: requiredList.length > 0,
                is_required: requiredList.length > 0,
                on_hold: requiredList.some(q => q.on_hold),
                // Statuses aggregated from required quotes only — disabled rows excluded
                designStatus: requiredList.length > 0
                    ? aggStatus(requiredList.map(q => q.design_status))
                    : null,
                design_status: requiredList.length > 0
                    ? aggStatus(requiredList.map(q => q.design_status))
                    : null,
                estimationStatus: requiredList.length > 0
                    ? aggStatus(requiredList.map(q => q.estimation_status))
                    : null,
                estimation_status: requiredList.length > 0
                    ? aggStatus(requiredList.map(q => q.estimation_status))
                    : null,
                invoiced: requiredList.length > 0 && requiredList.every(q => q.invoiced),
                expectedFinishDate: first.expected_finish_date,
                expected_finish_date: first.expected_finish_date,
                expectedDeliveryDate: first.expected_delivery_date,
                expected_delivery_date: first.expected_delivery_date,
                quote_type: quoteType,
                child_unique_id: first.child_unique_id,
                // Full list preserved for consumers that need all rows (including disabled)
                _quoteList: quoteList,
                designers,
                production,
                delivery,
            };

            if (requiredList.length > 1) {
                // Multiple required children of the same type (new-system multi-child job).
                // Key each one by its UUID so expandAssignmentsByQuoteType can see all of
                // them individually — exactly how schedule.js transformJobData works.
                requiredList.forEach(q => {
                    const qDelivery = job.job_deliveries?.find(
                        d => d.quote_type === quoteType &&
                            (d.child_unique_id === q.id || (!d.child_unique_id && !q.child_unique_id))
                    );
                    const qDesigners = designers.filter(d =>
                        d.childJobId ? d.childJobId === q.id : !q.child_unique_id
                    );
                    quotes[q.id] = {
                        ...aggregatedEntry,
                        // Override with this specific child's values
                        isRequired: q.is_required,
                        is_required: q.is_required,
                        on_hold: q.on_hold || false,
                        designStatus: q.design_status,
                        design_status: q.design_status,
                        estimationStatus: q.estimation_status,
                        estimation_status: q.estimation_status,
                        invoiced: q.invoiced || false,
                        sent_production: q.sent_production || false,
                        expectedFinishDate: q.expected_finish_date,
                        expected_finish_date: q.expected_finish_date,
                        expectedDeliveryDate: q.expected_delivery_date,
                        expected_delivery_date: q.expected_delivery_date,
                        child_unique_id: q.child_unique_id,
                        id: q.id,
                        designers: qDesigners,
                        delivery: qDelivery ? {
                            isRequired: qDelivery.is_required,
                            deliveryStatus: qDelivery.delivery_status,
                            deliveryDate: qDelivery.delivery_date,
                            assignedDriver: qDelivery.assigned_driver_id ? {
                                driverId: qDelivery.assigned_driver_id,
                                driverName: qDelivery.assigned_driver_name,
                            } : null,
                        } : null,
                    };
                });
            } else {
                // Single child or old-system job — keep the type-keyed aggregate entry
                // for backward compatibility with all existing code that reads quotes['roof'] etc.
                quotes[quoteType] = aggregatedEntry;
            }
        });
    }

    // Transform estimator
    const estimator = job?.job_estimators || null

    return {
        _id: job.id,
        uniqueId: job.unique_id,
        customerName: job.customer_name,
        customerNumber: job.customer_number,
        customerMail: job.customer_mail,
        carpenterSupervisorName: job.carpenter_supervisor_name,
        customerAddress: job.customer_address,
        jobType: job.job_type,
        mainStatus: job.main_status,
        currentStatus: job.current_status,
        estimationFinishDate: job.expected_estimation_finish_date,
        bookingDeliveryDate: job.booking_delivery_date,
        invoiced: job.invoiced,
        jobInformation: {
            urgentQuote: job.urgent_quote,
            urgentDesign: job.urgent_design
        },
        quotes: quotes,
        estimator: estimator,
        createdAt: job.created_at,
        updatedAt: job.updated_at,
        quote_sent: job?.quote_sent,
        on_hold: job?.on_hold
    };
};

/**
 * Search jobs with text query
 */
export const searchJobs = async (companyId, filters = {}) => {
    try {
        const { searchQuery, userId } = filters;

        let query = supabase
            .from('jobs')
            .select(`
                *,
                job_quotes(*),
                job_designers(*),
                job_estimators(*, estimator_user:users!job_estimators_user_id_fkey(username, full_name)),
                job_production(*),
                job_deliveries(*)
            `)
            .eq('company_id', companyId)
            .eq('deleted', false);

        // Apply text search with expanded fields
        if (searchQuery) {
            // Search in main job fields: unique_id, customer_name, customer_address, carpenter_supervisor_name
            // Note: job_type and main_status are filtered on the frontend instead
            query = query.or(`unique_id.ilike.%${searchQuery}%,customer_name.ilike.%${searchQuery}%,customer_address.ilike.%${searchQuery}%,carpenter_supervisor_name.ilike.%${searchQuery}%`);
        }

        // Apply user filter
        if (userId) {
            const { data: designerJobs } = await supabase
                .from('job_designers')
                .select('job_id')
                .eq('user_id', userId);

            const { data: estimatorJobs } = await supabase
                .from('job_estimators')
                .select('job_id')
                .eq('user_id', userId);

            const jobIds = [
                ...(designerJobs?.map(d => d.job_id) || []),
                ...(estimatorJobs?.map(e => e.job_id) || [])
            ];

            if (jobIds.length > 0) {
                query = query.in('id', jobIds);
            }
        }

        let { data, error } = await query.limit(50);

        if (error) throw error;

        // If searching by text, also search in estimator and designer names
        // This is done post-fetch because these are in related tables
        if (searchQuery && data) {
            const lowerQuery = searchQuery.toLowerCase();

            // Get jobs where estimator or designer names match
            const { data: estimatorMatches } = await supabase
                .from('job_estimators')
                .select(`
                    job_id,
                    name,
                    estimator_user:users!job_estimators_user_id_fkey(username, full_name)
                `)
                .or(`name.ilike.%${searchQuery}%`);

            const { data: designerMatches } = await supabase
                .from('job_designers')
                .select('job_id, name')
                .ilike('name', `%${searchQuery}%`);

            // Get unique job IDs from estimator/designer matches
            const estimatorJobIds = new Set(estimatorMatches?.map(e => e.job_id) || []);
            const designerJobIds = new Set(designerMatches?.map(d => d.job_id) || []);
            const relatedJobIds = [...estimatorJobIds, ...designerJobIds];

            // Fetch jobs that match by estimator/designer name but weren't in the initial results
            if (relatedJobIds.length > 0) {
                const existingJobIds = new Set(data.map(j => j.id));
                const newJobIds = relatedJobIds.filter(id => !existingJobIds.has(id));

                if (newJobIds.length > 0) {
                    const { data: additionalJobs } = await supabase
                        .from('jobs')
                        .select(`
                            *,
                            job_quotes(*),
                            job_designers(*),
                            job_estimators(*, estimator_user:users!job_estimators_user_id_fkey(username, full_name)),
                            job_production(*),
                            job_deliveries(*)
                        `)
                        .eq('company_id', companyId)
                        .eq('deleted', false)
                        .in('id', newJobIds);

                    // Combine results
                    data = [...data, ...(additionalJobs || [])];
                }
            }
        }

        return data?.map(transformJobData) || [];
    } catch (error) {
        console.error('Error searching jobs:', error);
        throw error;
    }
};

/**
 * Assign a designer to a job quote
 */
export const assignDesignerToJob = async (jobId, quoteType, designerId) => {
    try {
        // Check if designer assignment already exists
        const { data: existing } = await supabase
            .from('job_designers')
            .select('*')
            .eq('job_id', jobId)
            .eq('quote_type', quoteType)
            .eq('user_id', designerId)
            .single();

        if (existing) {
            return existing;
        }

        // Get designer info
        const { data: designer } = await supabase
            .from('users')
            .select('username, full_name')
            .eq('id', designerId)
            .single();

        // Insert new designer assignment
        const { data, error } = await supabase
            .from('job_designers')
            .insert({
                job_id: jobId,
                quote_type: quoteType,
                user_id: designerId,
                name: designer?.username || designer?.full_name,
                assigned_at: new Date().toISOString(),
                design_status: "in_progress"
            })
            .select()
            .single();

        if (error) throw error;

        return data;
    } catch (error) {
        console.error('Error assigning designer:', error);
        throw error;
    }
};


/**
 * Assign a designer to a job quote
 */
export const toggleQuoteHold = async (jobId, quoteType, currentStatus, user) => {
    try {
        const { data, error } = await supabase
            .from('job_quotes')
            .update({ on_hold: !currentStatus })
            .eq('job_id', jobId)
            .eq('quote_type', quoteType);

        if (error) throw error;

        const { error: logError } = await supabase
            .from('job_action_logs')
            .insert({
                job_id: jobId,
                user_id: user.id,
                user_name: user?.full_name || user?.username,
                user_role: user?.role,
                action: `quote_${currentStatus ? "resumed" : "paused"}`,
                details: `${quoteType} department for job ${jobId} ${currentStatus ? "resumed" : "paused"}`
            });

        if (logError) throw logError;

        return {
            success: true,
            message: currentStatus ? `${quoteType} department resumed` : `${quoteType} department paused`,
            data: data
        };
    } catch (error) {
        console.error('Error toggling department hold:', error);
        throw error;
    }
};

export const toggleLayoutOnly = async (jobId, quoteType, currentStatus, user, quoteId = null) => {
    try {
        let query = supabase
            .from('job_quotes')
            .update({ layout_only: !currentStatus });
        // New system: match by UUID for a precise single-row update.
        // Old system: fall back to job_id + quote_type.
        if (quoteId) {
            query = query.eq('id', quoteId);
        } else {
            query = query.eq('job_id', jobId).eq('quote_type', quoteType);
        }
        const { data, error } = await query;

        if (error) throw error;

        const { error: logError } = await supabase
            .from('job_action_logs')
            .insert({
                job_id: jobId,
                user_id: user.id,
                user_name: user?.full_name || user?.username,
                user_role: user?.role,
                action: `layout_only_${!currentStatus ? "enabled" : "disabled"}`,
                details: `${quoteType} department marked as ${!currentStatus ? "layout only" : "not layout only"}`
            });

        if (logError) throw logError;

        return { success: true };
    } catch (error) {
        console.error('Error toggling layout only:', error);
        throw error;
    }
};

export const toggleWaitingJob = async (jobId, quoteType, currentStatus, user, quoteId = null) => {
    try {
        // New system: use the job_quotes PK for a precise single-row update.
        // Old system: fall back to job_id + quote_type.
        let query = supabase
            .from('job_quotes')
            .update({ waiting_job: !currentStatus });
        if (quoteId) {
            query = query.eq('id', quoteId);
        } else {
            query = query.eq('job_id', jobId).eq('quote_type', quoteType);
        }
        const { error } = await query;

        if (error) throw error;

        const { error: logError } = await supabase
            .from('job_action_logs')
            .insert({
                job_id: jobId,
                user_id: user.id,
                user_name: user?.full_name || user?.username,
                user_role: user?.role,
                action: `waiting_job_${!currentStatus ? "enabled" : "disabled"}`,
                details: `${quoteType} department marked as ${!currentStatus ? "waiting job" : "not waiting"}`
            });

        if (logError) throw logError;

        return { success: true };
    } catch (error) {
        console.error('Error toggling waiting job:', error);
        throw error;
    }
};

export const toggleSentProduction = async (jobId, quoteType, currentStatus, user, quoteId = null) => {
    try {
        let query = supabase
            .from('job_quotes')
            .update({ sent_production: !currentStatus });
        if (quoteId) {
            query = query.eq('id', quoteId);
        } else {
            query = query.eq('job_id', jobId).eq('quote_type', quoteType);
        }
        const { error } = await query;
        if (error) throw error;

        const { error: logError } = await supabase
            .from('job_action_logs')
            .insert({
                job_id: jobId,
                user_id: user.id,
                user_name: user?.full_name || user?.username,
                user_role: user?.role,
                action: `sent_production_${!currentStatus ? 'enabled' : 'disabled'}`,
                details: `${quoteType} department ${!currentStatus ? 'sent to' : 'removed from'} production`,
            });
        if (logError) throw logError;

        return { success: true };
    } catch (error) {
        console.error('Error toggling sent_production:', error);
        throw error;
    }
};

export const toggleHold = async (jobId, currentStatus, user) => {
    try {

        // Insert new designer assignment
        const { data, error } = await supabase
            .from('jobs')
            .update({ on_hold: !currentStatus })
            .eq('id', jobId);

        if (error) throw error;

        // 6. Create action log
        const { error: logError } = await supabase
            .from('job_action_logs')
            .insert({
                job_id: jobId,
                user_id: user.id,
                user_name: user?.full_name || user?.username,
                user_role: user?.role,
                action: `job_${currentStatus ? "resumed" : "paused"}`,
                details: `Job ${jobId} ${currentStatus ? "resumed" : "paused"}`
            });

        if (logError) throw logError;

        return {
            success: true,
            message: currentStatus ? "Job is resumed" : "Job is paused",
            data: data
        };
    } catch (error) {
        console.error('Error assigning designer:', error);
        throw error;
    }
};